import { useEffect, useState } from 'react';
import { APP_SCOPE } from '@/lib/appScope';

/**
 * Service-worker registration and the two bits of state the UI needs from it:
 * whether an update is waiting, and whether anything is queued offline.
 *
 * Registration is `prompt` rather than auto-update on purpose. A driver
 * mid-delivery should not have the app reload under them because a deploy
 * landed — they tap when they are ready. The console is the same code path,
 * and the same reasoning applies to a dispatcher mid-dispatch.
 */

let updateCallback = null;
let registration = null;
/**
 * True for this page load when the worker being installed is not a deploy
 * but the other app's registration replacing this one — see
 * SCOPE_STORAGE_KEY below.
 *
 * Module scope, not a local inside registerServiceWorker(), and V-103 is
 * why: it started as a local, and useServiceWorkerUpdate()'s `recheck` —
 * the third path that can raise the prompt, fired by every visibilitychange
 * and every `online` event — could not see it. So the suppression held for
 * exactly as long as the driver did not switch tabs or regain signal, which
 * on a phone is seconds. Module scope IS the page load, which is the exact
 * lifetime this flag is supposed to have.
 */
let switchingApps = false;

/**
 * Which app last registered a worker on this origin, remembered across loads.
 *
 * V-97 gave the worker a `?app=` parameter so a driver's phone can precache
 * the Driver App instead of the whole console. The browser keys a
 * registration by its script URL, so opening the console and then the Driver
 * App in the SAME browser replaces one registration with the other — rare in
 * the field (a phone is a driver's, a desk is a dispatcher's) but not rare on
 * the machine of an owner showing a driver how the app works, which is
 * installation day.
 *
 * That replacement installs a worker while another one controls the page,
 * which is indistinguishable from a deploy — so without this the Driver App
 * would open with a "new version, tap to reload" prompt that is not a new
 * version. Remembering the last scope makes it distinguishable.
 */
const SCOPE_STORAGE_KEY = 'aragela_sw_app';

function lastRegisteredApp() {
  try {
    return window.localStorage.getItem(SCOPE_STORAGE_KEY);
  } catch {
    // Private mode, or storage disabled. Not knowing means treating the
    // install as a real update, which is the pre-V-97 behaviour.
    return null;
  }
}

function rememberRegisteredApp() {
  try {
    window.localStorage.setItem(SCOPE_STORAGE_KEY, APP_SCOPE);
  } catch {
    /* nothing to do: see above */
  }
}

export async function registerServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  // A worker registered against a dev server fights HMR; see devOptions in
  // vite.config.js. Production builds only.
  if (import.meta.env.DEV) return null;

  // A switch between the two apps on one browser, not a deploy.
  switchingApps = lastRegisteredApp() !== null && lastRegisteredApp() !== APP_SCOPE;
  rememberRegisteredApp();

  try {
    // The parameter is read by sw.js at install time and decides what gets
    // precached; the server ignores the query string and serves the same
    // file either way.
    registration = await navigator.serviceWorker.register(`/sw.js?app=${APP_SCOPE}`, { scope: '/' });

    // Already waiting when the page loaded (a previous visit fetched it).
    if (registration.waiting && !switchingApps) updateCallback?.();

    registration.addEventListener('updatefound', () => {
      const installing = registration.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        // `controller` is null on the very first install — that is a fresh
        // cache, not an update, and prompting for it would be nonsense. Nor
        // is a re-precache caused by the reader switching apps.
        if (installing.state === 'installed' && navigator.serviceWorker.controller && !switchingApps) {
          updateCallback?.();
        }
      });
    });

    return registration;
  } catch (error) {
    // A failed registration must never break the app: it only means no
    // offline support this session.
    console.warn('[pwa] service worker registration failed', error);
    return null;
  }
}

/** Applies a waiting update and reloads once the new worker takes over. */
export function applyUpdate() {
  if (!registration?.waiting) {
    window.location.reload();
    return;
  }
  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), {
    once: true,
  });
  registration.waiting.postMessage({ type: 'SKIP_WAITING' });
}

/**
 * True once a new version is installed and waiting. The caller renders a
 * "reload to update" affordance rather than reloading on its own.
 */
export function useServiceWorkerUpdate() {
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => {
    updateCallback = () => setUpdateReady(true);
    registerServiceWorker();

    // Telegram resumes the Mini App's WebView rather than reloading it, so a
    // worker that installed while the app sat in the background is never
    // noticed — registration, and with it the update check, only runs on a
    // real page load. Observed on a real phone: a driver reopened the app,
    // saw "laukia: 3", and got no update prompt, because the worker holding
    // the fix that drains that queue was sitting in `waiting` and nothing
    // ever looked again.
    //
    // Coming back to the foreground and regaining signal are the two moments
    // a phone plausibly has a new version to pick up, so both re-check.
    const recheck = () => {
      if (document.visibilityState !== 'visible') return;
      registration?.update().catch(() => {});
      // V-103: the same suppression the two paths in registerServiceWorker()
      // apply. Without it this path re-raised the prompt the moment a driver
      // switched tabs or regained signal, for a worker that is the other
      // app's registration rather than a new version.
      if (registration?.waiting && !switchingApps) setUpdateReady(true);
    };
    document.addEventListener('visibilitychange', recheck);
    window.addEventListener('online', recheck);

    return () => {
      updateCallback = null;
      document.removeEventListener('visibilitychange', recheck);
      window.removeEventListener('online', recheck);
    };
  }, []);

  return { updateReady, applyUpdate };
}

/** Reads workbox-background-sync's own store, read-only. */
async function countQueued() {
  if (typeof indexedDB === 'undefined') return null;
  return new Promise((resolve) => {
    const open = indexedDB.open('workbox-background-sync');
    open.onerror = () => resolve(null);
    // Never created yet means nothing has ever been queued, which is zero.
    open.onupgradeneeded = () => resolve(0);
    open.onsuccess = () => {
      const db = open.result;
      if (!db.objectStoreNames.contains('requests')) {
        db.close();
        return resolve(0);
      }
      const request = db.transaction('requests', 'readonly').objectStore('requests').count();
      request.onsuccess = () => {
        resolve(request.result);
        db.close();
      };
      request.onerror = () => {
        resolve(null);
        db.close();
      };
    };
  });
}

/**
 * Online/offline, and how many driver actions the worker is holding.
 *
 * `navigator.onLine` alone is famously unreliable — it reports true for a
 * wifi network with no route to the internet, which is exactly what a
 * quarry's site office looks like. It is used here only as the immediate
 * signal; the authoritative "did anything actually get through" answer is
 * the queue, which the worker reports as it drains.
 */
export function useOfflineState() {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const [queued, setQueued] = useState(0);
  const [justSynced, setJustSynced] = useState(0);
  const [justRejected, setJustRejected] = useState(0);

  useEffect(() => {
    // Telling the worker to drain is the whole point of handling `online`.
    // Workbox's Queue waits for a Background Sync `sync` event, which
    // Telegram's WebView and iOS never fire, so without this nudge a tap made
    // in the pit stays queued after the signal returns — which is the exact
    // failure this queue exists to prevent.
    const replayQueue = () => navigator.serviceWorker?.controller?.postMessage({ type: 'REPLAY_QUEUE' });
    const goOnline = () => {
      setOnline(true);
      replayQueue();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    const onMessage = (event) => {
      const data = event.data;
      if (!data?.type) return;
      if (data.type === 'queue-size') setQueued(data.size);
      if (data.type === 'queue-replayed') {
        setQueued(0);
        setJustSynced(data.count);
        // Long enough to notice from a cab, short enough not to linger.
        window.setTimeout(() => setJustSynced(0), 6000);
      }
      if (data.type === 'queue-rejected') {
        // V-177. The worker's own final word on a tap it could not
        // deliver — see sw.js's drainQueue(). Longer than justSynced's
        // window on purpose: this is the one message a driver has to
        // actually read and act on (redo the weight entry, check with
        // dispatch), not a confirmation that fades once seen.
        setJustRejected(data.count);
        window.setTimeout(() => setJustRejected(0), 15000);
      }
    };
    navigator.serviceWorker?.addEventListener('message', onMessage);

    // Reopening the app offline must show what is already waiting, not a
    // clean slate — the worker only pushes on change, so read the store once
    // at mount. Same IndexedDB the worker replays from.
    // Also nudge on mount, not just on the `online` event: a driver who
    // leaves the dead zone with the phone asleep and opens the app afterwards
    // never sees an `online` transition, because the app was not running when
    // it happened.
    countQueued().then((n) => {
      if (n == null) return;
      setQueued(n);
      if (n > 0 && navigator.onLine) replayQueue();
    });

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      navigator.serviceWorker?.removeEventListener('message', onMessage);
    };
  }, []);

  return { online, queued, justSynced, justRejected };
}
